import { useState } from "react"
import { App as AntdApp, Button, Form, Input, Modal, Select, Table, Tag, Tooltip, Typography } from "antd"
import type { TableColumnsType } from "antd"
import { KeyRound, Plus, UserRoundX, UserRoundCheck } from "lucide-react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { CreateStaffRequest, Role, StaffSummary, TenantSummary } from "@csbi/contracts"
import { ROLES, ROLE_DESCRIPTIONS, ROLE_LABELS } from "@csbi/contracts"
import { formatTimestamp } from "@csbi/viz-core"
import { createStaff, fetchStaff, resetStaffPassword, updateStaff } from "../../features/staff/api.js"
import { fetchTenants } from "../../features/tiles/api.js"
import { ALL, ScopePicker } from "./scope-picker.js"
import { ApiError } from "../../shared/api/request.js"
import { PageHeader } from "../../shared/components/page-header.js"

const ROLE_TONE: Record<Role, string> = { viewer: "default", analyst: "blue", admin: "gold" }

/*
 * ⚠ The generated password is shown **once**, here, and never again. Nothing mails it and no
 * endpoint returns it on a later read, so a dialog that is dismissed before the password is
 * copied leaves only one way forward: reset it again. The dialog says so rather than letting
 * someone discover it.
 */
function OneTimePassword({ password, account, onClose }: { password: string; account: string; onClose: () => void }) {
  const { message } = AntdApp.useApp()
  return (
    <Modal open title="One-time password" onCancel={onClose} onOk={onClose} okText="I have copied it" cancelButtonProps={{ style: { display: "none" } }}>
      <Typography.Paragraph>
        Give this to <strong>{account}</strong> through a channel you trust. They will be asked to change it after
        signing in.
      </Typography.Paragraph>
      <Typography.Paragraph copyable={{ text: password, onCopy: () => message.success("Copied") }}>
        <code style={{ fontSize: 15 }}>{password}</code>
      </Typography.Paragraph>
      <Typography.Text type="secondary">
        It is not stored anywhere in readable form. Once this dialog closes the only way to get a new one is another
        reset.
      </Typography.Text>
    </Modal>
  )
}

/** antd's Form injects `value`/`onChange`; ScopePicker takes the same pair, so this only fills the defaults in */
function ScopePickerField({
  tenants,
  loading,
  value,
  onChange,
}: {
  tenants: readonly TenantSummary[]
  loading: boolean
  value?: string[]
  onChange?: (next: string[]) => void
}) {
  return (
    <ScopePicker tenants={tenants} loading={loading} value={value ?? [ALL]} onChange={(n) => onChange?.(n)} />
  )
}

export function StaffPage() {
  const { message, modal } = AntdApp.useApp()
  const qc = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)
  const [issued, setIssued] = useState<{ account: string; password: string } | null>(null)
  const [form] = Form.useForm<CreateStaffRequest>()

  const { data: tenantList, isLoading: tenantsLoading } = useQuery({
    queryKey: ["tenants"],
    queryFn: ({ signal }) => fetchTenants(signal),
    staleTime: 5 * 60_000,
  })
  const tenants = tenantList?.tenants ?? []

  const { data, isLoading } = useQuery({
    queryKey: ["staff"],
    queryFn: ({ signal }) => fetchStaff(signal),
    staleTime: 10_000,
  })

  const refresh = () => void qc.invalidateQueries({ queryKey: ["staff"] })
  const fail = (err: unknown) => message.error(err instanceof ApiError ? err.message : "Something went wrong")

  const create = useMutation({
    mutationFn: createStaff,
    onSuccess: (created) => {
      setFormOpen(false)
      form.resetFields()
      setIssued({ account: created.account, password: created.initialPassword })
      refresh()
    },
    onError: fail,
  })

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof updateStaff>[1] }) => updateStaff(id, patch),
    onSuccess: () => { message.success("Saved"); refresh() },
    onError: fail,
  })

  const reset = useMutation({
    mutationFn: ({ id }: { id: string; account: string }) => resetStaffPassword(id),
    onSuccess: (result, vars) => {
      setIssued({ account: vars.account, password: result.initialPassword })
      message.success(`Ended ${result.sessionsClosed} session(s)`)
      refresh()
    },
    onError: fail,
  })

  const columns: TableColumnsType<StaffSummary> = [
    { title: "Account", dataIndex: "account", render: (a: string, r) => (r.active ? a : <span style={{ opacity: 0.5 }}>{a}</span>) },
    { title: "Name", dataIndex: "username" },
    {
      title: "Role",
      dataIndex: "role",
      render: (role: Role, r) => (
        <Tooltip title={ROLE_DESCRIPTIONS[role]}>
          <Select
            size="small"
            value={role}
            style={{ width: 150 }}
            disabled={update.isPending}
            onChange={(next) => update.mutate({ id: r.staffId, patch: { role: next } })}
            options={ROLES.map((x) => ({ value: x, label: ROLE_LABELS[x] }))}
          />
        </Tooltip>
      ),
    },
    {
      title: "Customers",
      dataIndex: "tenantScope",
      width: 320,
      render: (scope: string[], r) => (
        <ScopePicker
          size="small"
          tenants={tenants}
          loading={tenantsLoading}
          value={scope}
          onChange={(next) => update.mutate({ id: r.staffId, patch: { tenantScope: next.length ? next : [ALL] } })}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "active",
      render: (active: boolean, r) => (
        <Tag color={active ? ROLE_TONE[r.role] : "default"}>{active ? ROLE_LABELS[r.role] : "Disabled"}</Tag>
      ),
    },
    {
      title: "Last sign-in",
      dataIndex: "lastLoginAt",
      render: (at: string | null) => (at ? formatTimestamp(at) : <span style={{ opacity: 0.5 }}>never</span>),
    },
    {
      title: "Actions",
      render: (_, r) => (
        <>
          <Tooltip title="Issue a new one-time password and end every session">
            <Button
              type="text"
              size="small"
              icon={<KeyRound size={14} />}
              aria-label={`Reset the password for ${r.account}`}
              onClick={() =>
                modal.confirm({
                  title: `Reset the password for ${r.account}?`,
                  content: "Every session of theirs ends immediately, including any they have open.",
                  okText: "Reset",
                  onOk: () => reset.mutateAsync({ id: r.staffId, account: r.account }),
                })
              }
            />
          </Tooltip>
          <Tooltip title={r.active ? "Disable — ends their sessions and refuses sign-in" : "Enable"}>
            <Button
              type="text"
              size="small"
              icon={r.active ? <UserRoundX size={14} /> : <UserRoundCheck size={14} />}
              aria-label={`${r.active ? "Disable" : "Enable"} ${r.account}`}
              onClick={() => update.mutate({ id: r.staffId, patch: { active: !r.active } })}
            />
          </Tooltip>
        </>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Staff"
        extra={
          <Button type="primary" icon={<Plus size={14} />} onClick={() => setFormOpen(true)}>
            Add someone
          </Button>
        }
      />
      <Table
        rowKey="staffId"
        size="small"
        loading={isLoading}
        dataSource={data ?? []}
        columns={columns}
        pagination={false}
      />

      <Modal
        open={formOpen}
        title="Add someone"
        okText="Create"
        confirmLoading={create.isPending}
        onCancel={() => setFormOpen(false)}
        onOk={() => {
          void form.validateFields().then((values) =>
            create.mutate({
              account: values.account,
              username: values.username,
              role: values.role,
              tenantScope: values.tenantScope?.length ? values.tenantScope : [ALL],
            }),
          )
        }}
      >
        <Form form={form} layout="vertical" initialValues={{ role: "viewer", tenantScope: [ALL] }}>
          <Form.Item name="account" label="Email" rules={[{ required: true, type: "email", message: "Enter a valid email address" }]}>
            <Input placeholder="name@regdesk.co" autoComplete="off" />
          </Form.Item>
          <Form.Item name="username" label="Name" rules={[{ required: true, message: "Enter a name" }]}>
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name="role" label="Role" extra={ROLE_DESCRIPTIONS[(form.getFieldValue("role") as Role) ?? "viewer"]}>
            <Select options={ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }))} />
          </Form.Item>
          <Form.Item
            name="tenantScope"
            label="Customers"
            extra="Which deployments this person may see. It narrows what they can query; it never widens it."
          >
            <ScopePickerField tenants={tenants} loading={tenantsLoading} />
          </Form.Item>
        </Form>
      </Modal>

      {issued && (
        <OneTimePassword account={issued.account} password={issued.password} onClose={() => setIssued(null)} />
      )}
    </>
  )
}
